import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { ScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";
import Watermark from "@/components/base/Watermark";
import { homeFAQItems } from "@/mocks/faq";
import { CATALOG_URL } from "@/mocks/site-config";

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = "崧燁文化常見問題｜書籍、購書通路與品牌合作";

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "查看崧燁文化 Sunya 常見問題，了解品牌關係、正式書目查詢、合作購書通路、書籍上架狀態及品牌經銷與合作洽詢方式。");
    }
  }, []);

  return (
    <>
      <Navbar />
      <main className="relative">
        <Watermark />
        {/* Hero */}
        <section className="min-h-[100svh] flex items-center bg-background-50 pt-16 md:pt-[68px]">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="flex flex-col lg:flex-row lg:gap-16">
                <div className="lg:w-[30%] flex-shrink-0 mb-6 lg:mb-0">
                  <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 font-medium">
                    FAQ
                  </p>
                </div>
                <div className="lg:w-[70%] max-w-[620px]">
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-5">
                    崧燁文化常見問題
                  </h1>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    本頁整理崧燁文化品牌、正式出版書籍、書目查詢、合作購書通路及品牌合作等常見問題。完整出版資訊請以崧博出版書籍資料庫與各通路正式頁面為準。
                  </p>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    崧燁文化 Sunya 為崧博出版合作經銷品牌之一。完整書名、作者、ISBN與出版日期可由崧博出版書籍資料庫查詢；價格、庫存、版本與購買方式則以各合作通路頁面為準。崧燁文化不提供電子書、POD或有聲書等出版製作代工服務。
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* FAQ Items */}
        <section className="min-h-[100svh] flex items-center bg-background-100 relative z-[2]">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="lg:ml-[30%] lg:pl-16">
                <div className="divide-y divide-background-200/60 border-t border-background-200/60">
                  {homeFAQItems.map((faq, i) => (
                    <div key={i} className="py-4">
                      <button
                        onClick={() => setOpenIndex(openIndex === i ? null : i)}
                        className="w-full flex items-center justify-between gap-4 text-left cursor-pointer group"
                        aria-expanded={openIndex === i}
                      >
                        <h2 className="font-heading text-sm md:text-base font-medium text-foreground-900 group-hover:text-accent-600 transition-colors flex-1 leading-snug">
                          {faq.question}
                        </h2>
                        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                          <i
                            className={`ri-${
                              openIndex === i ? "subtract" : "add"
                            }-line text-base text-foreground-400 transition-transform duration-200 ${
                              openIndex === i ? "rotate-90" : ""
                            }`}
                          ></i>
                        </span>
                      </button>
                      {openIndex === i && (
                        <div className="mt-3 text-sm text-foreground-600 leading-relaxed pr-8">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA: Book Catalog */}
        <section className="min-h-[50svh] flex items-center bg-background-50">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="lg:ml-[30%] lg:pl-16 max-w-[620px]">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  查詢正式書目資訊
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  如需確認完整書名、作者、ISBN、出版日期及其他正式出版資料，請前往崧博出版書籍資料庫查詢。
                </p>
                <a
                  href={CATALOG_URL}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="bg-foreground-900 hover:bg-foreground-800 text-white px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground-400 focus-visible:ring-offset-2"
                >
                  前往崧博出版書籍資料庫 <i className="ri-external-link-line text-sm"></i>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA: Partner Stores */}
        <section className="min-h-[50svh] flex items-center bg-background-100 relative z-[2]">
          <div className="mx-auto px-6 md:px-10 py-16 md:py-24 max-w-[1120px] w-full">
            <ScrollReveal>
              <div className="lg:ml-[30%] lg:pl-16 max-w-[620px]">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  查看崧燁文化合作購書通路
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  不同書籍的上架平台可能不同，請由合作通路頁選擇適合的平台，並以各通路即時商品資訊為準。
                </p>
                <Link
                  to="/stores"
                  className="bg-accent-500 hover:bg-accent-600 text-white px-6 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors inline-flex items-center gap-2 justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2"
                >
                  查看合作購書通路 <i className="ri-store-2-line text-sm"></i>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}